import { defineComponent as _defineComponent } from 'vue'
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, watch } from "vue";
import {
  API_BIS_LISTLAYOUT,
  API_BIS_PATENT_BATCHDELETE,
  API_BIS_PATENT_LIST,
  API_BIS_PATENTTEXT,
} from "@/configs/Apis";
import colFormatters from "@/utils/ColumnFormater";
import { showConfirm, showSuccess, showWarning } from "@/utils/UI";
import { isEmpty, isEmptyOrWhiteSpace, trim } from "@/utils/Common";

import ProjectImportDialog from "@/views/project/components/ProjectImportDialog.vue";
import CustomField from "./components/CustomField.vue";
import DataClean from "./components/DataClean.vue";
import BatchIndex from "./components/BatchIndex.vue";
import BatchCategory from "./components/BatchCategory.vue";
import NavTreePanel from "./components/NavTreePanel.vue";
import CustomFilterPanel from "./components/CustomFilterPanel.vue";
import PatentSearchBar from "./components/PatentSearchBar.vue";
import ColumnConfigDialog from "./components/ColumnConfigDialog.vue";
import DataSync from "./components/DataSync.vue";
import DataSyncHistory from "@/views/components/DataSyncHistory.vue";

import PatentView from "@/views/patent/patent-view/PatentView.vue";
import Ipc from "@/views/components/IpcTag.vue";

import { CondItem, CondObject } from "@/types";
import { CondOperator } from "@/app-data/enum";
import { useStore } from "@/stores";
import DuduAjax from "@/utils/DuduAjax";
import router from "@/router";


export default /*#__PURE__*/_defineComponent({
  __name: 'PatentList',
  setup(__props) {

const store = useStore();

const dataTableRef = shallowRef<any>();
const patentViewRef = shallowRef<any>();
const batchIndexRef = shallowRef<any>();
const batchCategoryRef = shallowRef<any>();
const customFieldRef = shallowRef<any>();
const dataCleanRef = shallowRef<any>();
const columnConfigDialogRef = shallowRef<any>();
const wipsDataImportRef = shallowRef<any>();

const patentData = shallowRef<Array<any>>([]);

const condition = ref({
  dateField: "",
  dateQueryType: "",
  keyWord: "",
  textField: "",
  dateQueryRange: [] as Array<any>,
  dateQueryYear: null as any,
});

const pageSize = ref(20);
const pageIndex = ref(1);
const recordCount = ref(0);

const selectedItems = shallowRef<Nullable<Array<any>>>();

const advQuery = ref("");
const customQuery = ref("");

const nodeCondition = ref("");

/** 左右两侧筛选面板的引用: 用于在切换来源时清除另一侧的选中高亮 */
const navTreeRef = shallowRef();
const customFilterPanelRef = shallowRef();
/** 左侧导航分类面板宽度（可通过拖拽条调整） */
const navPanelWidth = ref(262);
const isResizing = ref(false);
const NAV_PANEL_MIN_WIDTH = 220;
const NAV_PANEL_MAX_WIDTH = 520;
let resizeStartX = 0;
let resizeStartWidth = 0;

const onNavResizeMove = (e: MouseEvent) => {
  const next = resizeStartWidth + (e.clientX - resizeStartX);
  navPanelWidth.value = Math.min(Math.max(next, NAV_PANEL_MIN_WIDTH), NAV_PANEL_MAX_WIDTH);
};

const onNavResizeEnd = () => {
  if (!isResizing.value) {
    return;
  }
  isResizing.value = false;
  document.removeEventListener("mousemove", onNavResizeMove);
  document.removeEventListener("mouseup", onNavResizeEnd);
  document.body.style.userSelect = "";
  document.body.style.cursor = "";
  dataTableRef.value?.doLayout();
};

const onNavResizeStart = (e: MouseEvent) => {
  isResizing.value = true;
  resizeStartX = e.clientX;
  resizeStartWidth = navPanelWidth.value;
  document.body.style.userSelect = "none";
  document.body.style.cursor = "col-resize";
  document.addEventListener("mousemove", onNavResizeMove);
  document.addEventListener("mouseup", onNavResizeEnd);
};

onBeforeUnmount(onNavResizeEnd);

const queryMode = ref<"standard" | "advance">("standard");

const orderProp = ref("");
const currentItem = shallowRef<any>({});

const importDialogRef = shallowRef<any>(null);

const projectId = computed(() => {
  const pInfo: any = (store.state as any).project.info || {};
  return pInfo.id || 0;
});

const isLoading = computed(() => store.state.isLoading);

const activeTabName = computed(() => {
  const userId = store.getters["user/user"].id;
  return userId === 4 || userId === 5 ? "2" : "1";
});

const configedColumns = computed(() =>
  JSON.parse((store.state as any).project.listLayout).filter((col: any) => col.hidden !== true)
);

const selectedIds = computed(() => (selectedItems.value || []).map((item) => item.id.toString()));

const navToReport = () => {
  router.push({ name: "report", params: { projectId: projectId.value } });
};

const historyVisible = ref(false);

const openDataSyncHistory = () => {
  historyVisible.value = true;
};

const importData = () => {
  importDialogRef.value.open(projectId.value);
};

const handleDataSync = () => {
  wipsDataImportRef.value.open();
};

const onColumnConfig = () => {
  (columnConfigDialogRef.value as any).open();
};

const onColumnWidthChange = (newWidth: number, oldWidth: number, column: any) => {
  const columns: Array<any> = JSON.parse((store.state as any).project.listLayout);
  columns
    .filter((col) => col.prop === column.property)
    .forEach((col) => {
      col.width = newWidth;
    });
  const layout = JSON.stringify(columns);
  store.commit("project/listLayout", layout);
  DuduAjax.post(API_BIS_LISTLAYOUT, { listName: "PATENT", layout, projectId: projectId.value }, false);
};

const onColumnConfigSaved = () => {
  (dataTableRef.value as any).doLayout();
};

const onNavTreeNodeClick = (node: any) => {
  if (!Object.prototype.hasOwnProperty.call(node, "patentFilter")) {
    return;
  }
  // 导航分类与自定义分类/筛选相互独立: 真正选中导航分类条件时清空自定义侧的条件与选中高亮
  const navCondition = node.patentFilter.filterCondition;
  if (!isEmptyOrWhiteSpace(navCondition)) {
    customQuery.value = "";
    customFilterPanelRef.value?.clearSelectedItems();
  }
  queryMode.value = "standard";
  nodeCondition.value = navCondition;
  reloadData();
};

const handleCustomSearch = (condition: string) => {
  if (!isEmptyOrWhiteSpace(condition)) {
    nodeCondition.value = "";
    navTreeRef.value?.clearSelectedItems();
  }
  queryMode.value = "standard";
  customQuery.value = condition;
  reloadData();
};

const loadPatentList = async () => {
  let conditionStr: string;
  switch (queryMode.value) {
    case "advance":
      conditionStr = trim(advQuery.value);
      break;
    case "standard":
    default:
      conditionStr = createSearchCondition();
      break;
  }
  const condObject = {
    condition: conditionStr,
    page: pageIndex.value - 1,
    pageSize: pageSize.value,
    orderBy: "",
  };
  if (!isEmpty(orderProp.value)) {
    condObject.orderBy = orderProp.value;
  }
  const { count, data } = await DuduAjax.post<{ count: number; data: Array<any> }>(
    {
      url: API_BIS_PATENT_LIST,
      path: { projectId: projectId.value },
    },
    condObject
  );
  recordCount.value = count;
  patentData.value = data;
};

const reloadData = (reset = true) => {
  if (reset) {
    pageIndex.value = 1;
  }
  loadPatentList();
};

// 数据同步完成后自动刷新列表
watch(
  () => (store.state as any).synctask.dataSyncTask.status,
  (status, prev) => {
    if (prev === "RUNNING" && status !== "RUNNING") {
      // 稍作延迟，等待后端完成入库提交后再查询
      setTimeout(() => reloadData(), 1500);
    }
  }
);

const handleDataChange = () => {
  reloadData();
};

const onPatentDetailRefresh = () => {
  reloadData(false);
};

const onSelectionChanged = (items: Array<any>) => {
  selectedItems.value = items;
};

const getFormatters = (prop: string) => {
  if (isEmptyOrWhiteSpace(prop)) {
    return null;
  }
  const chars = prop.split("");
  chars[0] = chars[0].toUpperCase();
  const formatKey = `format${chars.join("")}`;
  return (
    colFormatters[formatKey] ||
    function (data: any, column: any, value: any) {
      return value;
    }
  );
};

const onDataCommand = (command: string, row: any, event: MouseEvent) => {
  event.stopPropagation();
  currentItem.value = row;
  (dataTableRef.value as any).setCurrentRow(row);
  if (command === "patentNo") {
    (patentViewRef.value as any).open();
  }
};

const onBatchOpCommand = (command: string) => {
  if (command === "delete") {
    deletePatent();
  } else if (command === "patenttext") {
    updatePatentText();
  } else if (command === "clean") {
    (dataCleanRef.value as any).open();
  } else if (command === "indexing") {
    batchIndexing();
  } else if (command === "category") {
    batchCategory();
  }
};

const deletePatent = () => {
  if (!selectedItems.value || !selectedItems.value.length) {
    return showWarning("请选择要删除的专利");
  }
  showConfirm("是否删除选择的专利").then(() => {
    const pId = (store.state as any).project.info.id;
    DuduAjax.post(
      {
        url: API_BIS_PATENT_BATCHDELETE,
        path: { projectId: pId },
      },
      (selectedItems.value || []).map((item) => item.id)
    ).then(() => {
      showSuccess("删除成功");
      reloadData();
    });
  });
};

const batchIndexing = () => {
  if (!selectedItems.value || !selectedItems.value.length) {
    return showWarning("请选择要标引的专利");
  }
  (batchIndexRef.value as any).open();
};

const batchCategory = () => {
  if (!selectedItems.value || !selectedItems.value.length) {
    return showWarning("请选择要分类的专利");
  }
  (batchCategoryRef.value as any).open();
};

const handleSearch = (searchCondition: any) => {
  queryMode.value = "standard";
  condition.value = searchCondition;
  (dataTableRef.value as any).clearSelection();
  reloadData();
};

const handleAdvSearch = (condStr: string) => {
  queryMode.value = "advance";
  advQuery.value = condStr;
  (dataTableRef.value as any).clearSelection();
  reloadData();
};

const onSortChange = ({ prop, order }: any) => {
  const field: string = prop;
  if (isEmpty(order)) {
    orderProp.value = "";
  } else {
    orderProp.value = `${field} ${order.replace("ending", "")}`;
  }
};

const closeImportDialog = async (status: string) => {
  if (status === "COMPLETED") {
    await store.dispatch("project/setCustomField", (store.state as any).project.info);
    reloadData();
  }
};

const openMetaData = () => {
  (customFieldRef.value as any).open();
};

const updatePatentText = () => {
  const task = (store.state as any).synctask.patentTextTask;
  if (task.status === "RUNNING") {
    showWarning("有一个正在执行的任务，请等待任务完成后再更新");
    return;
  }
  showConfirm("是否更新代码化全文/法律状态").then(() => {
    DuduAjax.post(API_BIS_PATENTTEXT, { projectId: projectId.value }).then((taskId) => {
      showSuccess("数据更新任务已提交到后台");
      return store.dispatch("synctask/progressPatentText", { taskId, projectId: projectId.value });
    });
  });
};

const onBodyClick = (e: MouseEvent) => {
  if (e) {
    e.stopPropagation();
  }
  (patentViewRef.value as any).close();
};

const createSearchCondition = () => {
  const condItems: Array<CondItem> = [];
  if (!isEmpty(condition.value.dateField) && !isEmpty(condition.value.dateQueryType)) {
    const condItem: CondItem = {
      name: condition.value.dateField,
      operator: CondOperator.BETWEEN,
      value1: "",
      value2: "",
    };

    if (condition.value.dateQueryType === "year" && condition.value.dateQueryYear) {
      const year = (condition.value.dateQueryYear as any as Date).getFullYear();
      switch (condition.value.dateField) {
        case "appDate":
        case "pubDate":
        case "authDate":
        case "modifiedTime":
          condItem.value1 = year + "-01-01";
          condItem.value2 = year + "-12-31";
          break;
        default:
          break;
      }
    } else if (
      condition.value.dateQueryType === "range" &&
      condition.value.dateQueryRange &&
      condition.value.dateQueryRange.length === 2
    ) {
      switch (condition.value.dateField) {
        case "appDate":
        case "pubDate":
        case "authDate":
        case "modifiedTime":
          condItem.value1 = condition.value.dateQueryRange[0];
          condItem.value2 = condition.value.dateQueryRange[1];
          break;
        default:
          break;
      }
    }
    if (!isEmptyOrWhiteSpace(condItem.value1)) {
      condItems.push(condItem);
    }
  }

  if (!isEmpty(condition.value.textField) && !isEmpty(condition.value.keyWord)) {
    condItems.push({
      name: condition.value.textField,
      operator: CondOperator.LIKE,
      value1: condition.value.keyWord,
      value2: "",
    });
  }

  if (!isEmpty(nodeCondition.value)) {
    const nodeCondObject: CondObject = JSON.parse(nodeCondition.value);
    if (nodeCondObject.items && nodeCondObject.items.length) {
      condItems.push(...nodeCondObject.items);
    }
  }

  const condObject: CondObject = { items: [], relation: "" };
  condObject.items = condItems;
  // 占位符不能与 relation 中已有的字符冲突，否则 AND 中的 D 会被一起替换成序号（如 "(1 AN2 3)"）
  const PLACEHOLDER = "#";
  const indexes: Array<string> = [...condItems.map(() => PLACEHOLDER)];

  if (!isEmptyOrWhiteSpace(customQuery.value)) {
    const nodeQuery = JSON.parse(trim(customQuery.value) || "{}") || {};
    if (nodeQuery.items && nodeQuery.items.length > 0) {
      condObject.items.push(...nodeQuery.items);
      indexes.push(`(${(nodeQuery.relation || "").replace(/\d+/g, PLACEHOLDER)})`);
    }
  }
  let index = 1;
  condObject.relation = indexes.join(" and ").replace(/#/g, () => (index++).toString());
  return JSON.stringify(condObject);
};

const onCurrentPageChange = (page: number) => {
  pageIndex.value = page;
  reloadData(false);
};

const onPageSizeChange = (size: number) => {
  pageSize.value = size;
  reloadData();
};

onMounted(() => {
  reloadData();
});

return { __sfc: true,store, dataTableRef, patentViewRef, batchIndexRef, batchCategoryRef, customFieldRef, dataCleanRef, columnConfigDialogRef, wipsDataImportRef, patentData, condition, pageSize, pageIndex, recordCount, selectedItems, advQuery, customQuery, nodeCondition, navTreeRef, customFilterPanelRef, navPanelWidth, isResizing, NAV_PANEL_MIN_WIDTH, NAV_PANEL_MAX_WIDTH, resizeStartX, resizeStartWidth, onNavResizeMove, onNavResizeEnd, onNavResizeStart, queryMode, orderProp, currentItem, importDialogRef, projectId, isLoading, activeTabName, configedColumns, selectedIds, navToReport, historyVisible, openDataSyncHistory, importData, handleDataSync, onColumnConfig, onColumnWidthChange, onColumnConfigSaved, onNavTreeNodeClick, handleCustomSearch, loadPatentList, reloadData, handleDataChange, onPatentDetailRefresh, onSelectionChanged, getFormatters, onDataCommand, onBatchOpCommand, deletePatent, batchIndexing, batchCategory, handleSearch, handleAdvSearch, onSortChange, closeImportDialog, openMetaData, updatePatentText, onBodyClick, createSearchCondition, onCurrentPageChange, onPageSizeChange, isEmptyOrWhiteSpace, ProjectImportDialog, CustomField, DataClean, BatchIndex, BatchCategory, NavTreePanel, CustomFilterPanel, PatentSearchBar, ColumnConfigDialog, DataSync, DataSyncHistory, PatentView, Ipc }
}

})